import { csvStringFromReactCsvPayload } from './cashflowCsvExportUtils';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const BREAKDOWN_PERIODS = 25;

const formatPeriodDate = ({ year, month }) =>
  `${MONTH_LABELS[month - 1]} ${year}`;

const addMonths = ({ year, month, day }, n) => {
  const total = year * 12 + (month - 1) + n;
  return { year: Math.floor(total / 12), month: (total % 12) + 1, day };
};

const monthsDiff = (laterDate, earlierDate) =>
  (laterDate.year - earlierDate.year) * 12 +
  (laterDate.month - earlierDate.month);

const isPredictedRevenueItem = (item) =>
  item?.IsPredicted === true || item?.is_predicted === true;

/**
 * @param {{
 *   cohortRevenueBreakdown?: unknown[],
 *   cohort_revenue_breakdown?: unknown[],
 *   items?: Record<string, unknown>,
 * }} calcResult
 * @returns {{ headers: { label: string, key: string }[], data: Record<string, unknown>[] } | null}
 */
export function buildCohortRevenueBreakdownCsvPayload(calcResult) {
  const cohortRevenueBreakdown =
    calcResult?.cohortRevenueBreakdown ??
    calcResult?.cohort_revenue_breakdown;
  if (!cohortRevenueBreakdown || cohortRevenueBreakdown.length === 0) {
    return null;
  }

  const items = calcResult?.items ?? calcResult;
  const totalRevenue =
    items?.profit_and_loss?.total_revenue ??
    items?.profitAndLoss?.totalRevenue ??
    [];

  const firstPredicted = totalRevenue.find(isPredictedRevenueItem)?.date;
  if (!firstPredicted) {
    return null;
  }

  const predictedPeriods = Array.from({ length: BREAKDOWN_PERIODS }, (_, i) =>
    addMonths(firstPredicted, i)
  );

  const headers = [
    { label: 'Cohort Date', key: 'cohort_date' },
    { label: 'Type', key: 'type' },
    ...predictedPeriods.map((periodDate, idx) => ({
      label: formatPeriodDate(periodDate),
      key: `period_${idx}`,
    })),
  ];

  const rows = cohortRevenueBreakdown.map((item) => {
    const cohortDate = item.cohort_date
      ? `${item.cohort_date.year}-${String(item.cohort_date.month).padStart(2, '0')}-${String(item.cohort_date.day).padStart(2, '0')}`
      : '';

    const row = {
      cohort_date: cohortDate,
      type: item.is_new ? 'new' : 'existing',
    };

    predictedPeriods.forEach((periodDate, idx) => {
      const mob = item.cohort_date
        ? monthsDiff(periodDate, item.cohort_date)
        : -1;
      const revenue = mob >= 0 ? (item.mob_revenues?.[mob] ?? 0) : 0;
      row[`period_${idx}`] = revenue !== 0 ? revenue.toFixed(2) : '';
    });

    return row;
  });

  return { headers, data: rows };
}

/** @param {Record<string, unknown> | null | undefined} calcResult */
export function buildCohortRevenueBreakdownCsvString(calcResult) {
  const payload = buildCohortRevenueBreakdownCsvPayload(calcResult);
  if (!payload) {
    return '';
  }
  return csvStringFromReactCsvPayload(payload);
}
